/**
 * Answering a question about a rule, mid-season — turn `7a`.
 *
 * GROUNDED IN A REAL CASE, and that is the point of building it. "Does a franchise tag limit a GM
 * to five keepers?" is answered today in a code comment in `luxuryTax.ts` — "Confirmed by the
 * commissioner, 2026" — a decision that exists in the app and nowhere the league can see it, and
 * which 2015-I-4 says a three-person panel should have made. This module is that panel.
 *
 * WHY NOT THE COMMITTEE. A quorum of eight and a super-majority of seven is right for writing
 * rules and wrong for answering a question in the middle of Week 7 with two trades waiting on it.
 * Three panelists, most votes wins, and the answer holds for the rest of that season only.
 *
 * THE DESIGN WORK IS THE COMPOSITION, and it is the half nobody reconstructs correctly in a group
 * chat. The panel is two co-commissioners and one rotating GM — DERIVED, never chosen:
 *
 *   A co-commissioner who stands to benefit RECUSES, and his seat passes to the most recent
 *   lottery winner. Rajan holds the only franchise tag this season, so he cannot sit on the panel
 *   deciding what a franchise tag costs.
 *
 *   The rotating seat is last season's LAST-PLACE finisher — unless a co-commissioner took the
 *   first overall pick or the lowest point total, in which case it walks down the point standings
 *   until it lands on somebody who is not already seated.
 *
 * Both cascades are worked out here and stated on screen, seat by seat, with the reason. A panel
 * whose composition has to be argued about is a panel whose answer will be argued about.
 */

export type Seat = 'co-commissioner' | 'replacement' | 'rotating';

export interface PanelSeat {
  gm: string;
  seat: Seat;
  /** Why this man holds this seat, in the words the screen prints. */
  why: string;
  /** Set on a commissioner who stood down, so the screen can show the seat he vacated. */
  recusedFor?: string;
}

export interface PanelInput {
  /** The co-commissioners, in roster order. */
  commissioners: readonly string[];
  /** Anybody who stands to benefit from the answer and must not sit. */
  conflicted: readonly string[];
  /** Last season's finish, champion first — the rotating seat walks up from the bottom. */
  finishOrder: readonly string[];
  /** Last season's point standings, highest first. Used when the simple cascade is blocked. */
  pointOrder?: readonly string[];
  /** Who won the most recent draft lottery. Takes a recused commissioner's seat. */
  lotteryWinner?: string | null;
}

const up = (s: string) => s.toUpperCase();
const eq = (a?: string | null, b?: string | null) => Boolean(a && b && up(a) === up(b));

/**
 * Who sits, and why.
 *
 * Returns three seats where it can. Fewer means the cascade ran out of people, which is a real
 * state — a two-man league of eligible panelists cannot be papered over — and the screen says so
 * rather than seating somebody it should not.
 */
export function panelFor(input: PanelInput): PanelSeat[] {
  const conflicted = new Set(input.conflicted.map(up));
  const seats: PanelSeat[] = [];
  const taken = new Set<string>();

  const seat = (gm: string, kind: Seat, why: string, recusedFor?: string) => {
    if (taken.has(up(gm))) return false;
    taken.add(up(gm));
    seats.push({ gm, seat: kind, why, ...(recusedFor ? { recusedFor } : {}) });
    return true;
  };

  for (const c of input.commissioners) {
    if (!conflicted.has(up(c))) {
      seat(c, 'co-commissioner', 'Co-commissioner.');
      continue;
    }
    /*
     * A conflicted commissioner's seat goes to the most recent LOTTERY WINNER rather than to
     * whoever is available. It is a rule of thumb with a real property: the lottery winner is
     * decided by chance months earlier, so nobody can arrange to be the replacement.
     */
    const stand = input.lotteryWinner;
    if (stand && !conflicted.has(up(stand)) && !taken.has(up(stand))) {
      seat(stand, 'replacement', `Replacing ${title(c)} · won the most recent lottery`, c);
    }
  }

  /*
   * The rotating seat: last place, walking up. The simple answer is right almost every year, and
   * the exception — a co-commissioner who took the first overall pick or the lowest point total —
   * is what the cascade exists for.
   */
  const bottomUp = [...input.finishOrder].reverse();
  const order = input.pointOrder && input.pointOrder.length
    ? [...input.pointOrder].reverse()
    : bottomUp;

  const rotate = (list: readonly string[], why: (gm: string) => string) => {
    for (const gm of list) {
      if (conflicted.has(up(gm)) || taken.has(up(gm))) continue;
      return seat(gm, 'rotating', why(gm));
    }
    return false;
  };

  const place = (gm: string) => {
    const i = input.finishOrder.findIndex((g) => eq(g, gm));
    return i < 0 ? '' : ` · ${ordinalOf(i + 1, input.finishOrder.length)} in the last season`;
  };

  /*
   * THE CASCADE, exactly as the drawing states it.
   *
   * The rotating seat is last season's last-place finisher. But if THAT MAN is already on the
   * panel — a co-commissioner who took the first overall pick — the seat does not simply slide to
   * second-from-last: it moves down the POINT standings instead. The two orders differ (Ari went
   * 10-4 and finished third), and picking the wrong one is how the seat lands on somebody who can
   * argue he should not have had it.
   */
  const naturally = bottomUp[0];
  const blocked = Boolean(naturally && (taken.has(up(naturally)) || conflicted.has(up(naturally))));

  if (blocked && input.pointOrder?.length) {
    rotate(order, (gm) => `Rotating GM · lowest point total available${place(gm)}`);
  } else {
    rotate(bottomUp, (gm) => `Rotating GM${place(gm)}`);
  }
  // Whatever happened above, the seat must be filled if anybody at all is eligible.
  if (!seats.some((x) => x.seat === 'rotating')) {
    rotate(bottomUp, (gm) => `Rotating GM${place(gm)}`);
  }

  return seats;
}

const title = (gm: string) => gm.charAt(0).toUpperCase() + gm.slice(1).toLowerCase();

function ordinalOf(n: number, of: number): string {
  if (n === of) return 'last';
  const s = n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd'
    : n % 10 === 3 && n !== 13 ? 'rd' : 'th';
  return `${n}${s}`;
}

/* -- the answer ------------------------------------------------------------------------------ */

export interface PanelTally {
  /** Answer text → who gave it. */
  byAnswer: { answer: string; who: string[] }[];
  /** Panelists who have not answered. */
  waiting: string[];
  /** The answer with the most votes, when there is one. */
  leading?: string;
  /**
   * TRUE WHEN THE OUTSTANDING VOTES CANNOT CHANGE THE RESULT.
   *
   * The useful question, and not the same as "everybody has answered". Two of three settles it;
   * the third man's vote would not change which interpretation leads, so a commissioner may
   * record it now or wait — and the screen offers both rather than pretending it must wait.
   */
  decidable: boolean;
  /** Why it is or is not decidable, in the sentence the screen prints. */
  why: string;
}

export function panelTally(
  panel: readonly PanelSeat[],
  answers: Readonly<Record<string, string>>,
): PanelTally {
  const seated = panel.map((s) => up(s.gm));
  const given = seated.filter((g) => answers[g]);
  const waiting = seated.filter((g) => !answers[g]);

  const byAnswer = new Map<string, string[]>();
  for (const g of given) {
    const a = answers[g]!;
    byAnswer.set(a, [...(byAnswer.get(a) ?? []), g]);
  }
  const ranked = [...byAnswer.entries()]
    .map(([answer, who]) => ({ answer, who }))
    .sort((a, b) => b.who.length - a.who.length || a.answer.localeCompare(b.answer));

  const top = ranked[0];
  const second = ranked[1];
  // Could the men still to answer catch the leader? If they all backed one rival, would it tie?
  const catchable = top ? (second?.who.length ?? 0) + waiting.length >= top.who.length : true;
  const decidable = Boolean(top) && !catchable;

  return {
    byAnswer: ranked,
    waiting,
    ...(top ? { leading: top.answer } : {}),
    decidable,
    why: !top
      ? 'Nobody has answered yet.'
      : decidable
        ? waiting.length === 0
          ? 'All three have answered.'
          : `${waiting.map(title).join(' and ')}${waiting.length === 1 ? '’s vote' : '’ votes'} `
            + 'would not change which interpretation has the most votes. A commissioner can '
            + 'record it now or wait.'
        : `${waiting.length} still to answer, and it could still go either way.`,
  };
}

/* -- what a recorded interpretation is -------------------------------------------------------- */

export interface Interpretation {
  ruleId: string;
  section?: string;
  season: number;
  question: string;
  raisedBy: string;
  raisedAt: string;
  /** Answers keyed by GM. Free text so a panel is not forced into yes/no on a reading question. */
  answers?: Record<string, string>;
  decision?: string;
  decidedAt?: string;
  /** Trades or other business held up until this is answered. */
  blocking?: string[];
}

/**
 * How it is cited afterwards — `2025 P05 §2 — interp. 2026`.
 *
 * The suffix is load-bearing. A panel answers for a season; only the committee writes rule text,
 * so an interpretation that cited itself as though it were the rule would quietly promote a
 * three-man decision into the rulebook.
 */
export function citeAs(i: Pick<Interpretation, 'ruleId' | 'section' | 'season'>): string {
  return `${i.ruleId}${i.section ? ` ${i.section}` : ''} — interp. ${i.season}`;
}

/**
 * How long it binds, and what it explicitly does NOT do.
 *
 * Both halves matter. Earlier weeks are not re-scored — a reading settled in Week 7 does not
 * reach back and change Week 3, or every interpretation would be a retroactive rule change. And
 * it does not carry into the next season on its own, which is why it is auto-docketed.
 */
export function holdsUntil(i: Pick<Interpretation, 'season' | 'decidedAt'>): string {
  const from = i.decidedAt
    ? new Date(i.decidedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  return `The end of the ${i.season} season${from ? `, from ${from}` : ''}. Earlier weeks are not `
    + `re-scored, and it does not carry into ${i.season + 1} on its own.`;
}

/* -- then codified --------------------------------------------------------------------------- */

/**
 * The proposal a recorded interpretation writes for the following year.
 *
 * THE PANEL ANSWERS FOR A SEASON; ONLY THE COMMITTEE WRITES RULE TEXT. Left alone, an
 * interpretation would either expire in December and take the answer with it, or quietly harden
 * into custom that nobody voted for — and the league has both kinds already. So recording one
 * also puts it on next year's pile.
 *
 * A DRAFT, NOT A SUBMISSION — Tim's call, and the right one. The panel settled what the rule
 * MEANS; turning that into rule text is a different job with different words, and a proposal that
 * arrived pre-submitted would be voted on wording nobody wrote. It lands where a commissioner can
 * edit it before the deadline, and it says where it came from so the meeting knows it is not a
 * new idea but a reading the league has already been running under.
 */
export interface CodifyDraft {
  season: number;
  gm: string;
  title: string;
  body: string;
  amends: string[];
  status: 'draft';
  createdAt: string;
  updatedAt: string;
}

export function codifyDraft(
  i: Pick<Interpretation, 'ruleId' | 'section' | 'season' | 'question' | 'decision'>,
  by: string,
  now: string,
): CodifyDraft {
  return {
    // NEXT year. The interpretation already covers this one, and codifying it mid-season would
    // mean voting on a rule that is already in force, which is not a vote.
    season: i.season + 1,
    gm: by.toUpperCase(),
    title: `Codify: ${i.question.replace(/\?+$/, '')}`,
    body: [
      `Section 1: ${i.decision ?? ''}`.trim(),
      '',
      `Explanation: the panel answered this on ${i.ruleId}${i.section ? ` ${i.section}` : ''} `
      + `during ${i.season} and the league played the rest of the season under it. An `
      + 'interpretation binds for one season only, so it is here to be written into the rule or '
      + 'dropped deliberately rather than allowed to lapse by nobody raising it.',
      '',
      `The question as raised: ${i.question}`,
    ].join('\n'),
    amends: [i.ruleId],
    status: 'draft',
    createdAt: now,
    updatedAt: now,
  };
}
